import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser, fetch_user_profile, updateEmailConfirmationStatus } from '../../redux/auth-slice';
import { supabase } from '../../utils/supabase-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { LoaderCircle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../ui/use-toast';
import axiosInstance from '../../utils/axios-auth';
import { syncPendingStudentSignupComplianceFromAuthUser } from '../../utils/sync-pending-student-signup-compliance';
function AuthCallback() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState('Processing your verification...');
    // Safety timeout to prevent infinite loading screens
    const [showManualAction, setShowManualAction] = useState(false);
    // Track processed tokens to prevent double-processing (Race Condition Fix)
    // We track the actual value to allow re-processing if the URL changes (e.g. redirect adds hash)
    const processedHashRef = useRef(null);
    const processedCodeRef = useRef(null);
    useEffect(() => {
        // Safety timer: If nothing happens in 10 seconds, allow user to take action
        const safetyTimer = setTimeout(() => {
            if (status === 'loading') {
                setShowManualAction(true);
                setMessage('Verification is taking longer than expected. You can try refreshing or logging in.');
            }
        }, 10000);
        return () => clearTimeout(safetyTimer);
    }, [status]);
    useEffect(() => {
        let navigationTimer;
        // Helper function to handle session setup and redirection (DRY)
        const setupSessionAndRedirect = async (currentSession) => {
            try {
                console.log('[AuthCallback] Starting session setup...');
                // Timeout wrapper to prevent infinite loading
                const sessionSetupPromise = async () => {
                    // 1. Validate Session
                    if (!currentSession?.access_token) {
                        throw new Error('Invalid session: missing access token');
                    }
                    // Force session refresh to ensure we have the latest claims and user data
                    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
                    if (refreshError) {
                        console.warn('[AuthCallback] Session refresh warning:', refreshError);
                        // Continue anyway as we have a valid token
                    }
                    // Use the refreshed session if available (ensures email_confirmed_at is up-to-date)
                    let supabaseUser = refreshData?.session?.user;
                    // Fallback to getUser() if refresh didn't return user data
                    if (!supabaseUser) {
                        console.log('[AuthCallback] Falling back to getUser()');
                        const { data: userData, error: userError } = await supabase.auth.getUser();
                        if (userError)
                            throw userError;
                        if (!userData.user)
                            throw new Error('Failed to retrieve user details.');
                        supabaseUser = userData.user;
                    }
                    console.log('[AuthCallback] User data retrieved:', {
                        id: supabaseUser.id,
                        email: supabaseUser.email,
                        email_confirmed_at: supabaseUser.email_confirmed_at,
                        metadata_verified: supabaseUser.user_metadata?.email_verified
                    });
                    // Enhanced confirmation check:
                    // 1. Check strict email_confirmed_at timestamp (DB source of truth)
                    // 2. Check user_metadata.email_verified (available in the fresh token)
                    // This ensures users who just clicked the link are recognized as confirmed
                    // even if the DB read is slightly behind the token generation.
                    const isEmailConfirmed = !!supabaseUser.email_confirmed_at ||
                        (supabaseUser.user_metadata?.email_verified === true);
                    console.log(`[AuthCallback] Email confirmation status: ${isEmailConfirmed}`, {
                        confirmedAt: supabaseUser.email_confirmed_at,
                        metaVerified: supabaseUser.user_metadata?.email_verified
                    });
                    // 3. Prepare User Data (With Robust Role Detection)
                    let userRole = supabaseUser.user_metadata?.role;
                    // Mismatch Fix: If role is missing or generic 'authenticated', try to detect it from DB
                    if (!userRole || userRole === 'authenticated') {
                        console.log('[AuthCallback] Role detection needed. Checking database...');
                        try {
                            // Try to find if user is a tutor
                            const { data: tutorData } = await supabase.rpc('get_user_profile_by_id', {
                                user_id: supabaseUser.id,
                                user_role: 'tutor'
                            });
                            if (tutorData) {
                                userRole = 'tutor';
                            }
                            else {
                                // Check if user is a student
                                const { data: studentData } = await supabase.rpc('get_user_profile_by_id', {
                                    user_id: supabaseUser.id,
                                    user_role: 'student'
                                });
                                if (studentData) {
                                    userRole = 'student';
                                }
                                else {
                                    // Default to student if detection fails
                                    console.warn('[AuthCallback] Role detection failed, defaulting to student');
                                    userRole = 'student';
                                }
                            }
                            console.log(`[AuthCallback] Role detected as: ${userRole}`);
                        }
                        catch (detectError) {
                            console.error('[AuthCallback] Error detecting role:', detectError);
                            userRole = 'student'; // Fallback
                        }
                    }
                    const fetchArgs = {
                        userId: supabaseUser.id,
                        userRole: userRole ?? 'student', // Use detected role
                        userEmail: supabaseUser.email ?? null
                    };
                    const initialUser = {
                        id: fetchArgs.userId,
                        email: fetchArgs.userEmail,
                        role: fetchArgs.userRole,
                        first_name: supabaseUser.user_metadata?.first_name ?? null,
                        last_name: supabaseUser.user_metadata?.last_name ?? null,
                    };
                    // 4. Update Redux State
                    await dispatch(setUser({
                        user: initialUser,
                        session: {
                            access_token: currentSession.access_token,
                            refresh_token: currentSession.refresh_token
                        }
                    }));
                    // 5. Fetch Full Profile
                    // We await this but handle failure gracefully so we don't block access
                    let finalUser = initialUser;
                    try {
                        const profileResultAction = await dispatch(fetch_user_profile(fetchArgs));
                        if (fetch_user_profile.fulfilled.match(profileResultAction)) {
                            finalUser = { ...initialUser, ...profileResultAction.payload, id: initialUser.id };
                        }
                    }
                    catch (err) {
                        console.warn('Profile fetch failed, continuing with basic user data:', err);
                    }
                    if (fetchArgs.userRole === 'student') {
                        try {
                            await syncPendingStudentSignupComplianceFromAuthUser(supabaseUser);
                        }
                        catch (complianceErr) {
                            console.warn('[AuthCallback] Student signup compliance sync skipped:', complianceErr);
                        }
                    }
                    // 6. Update Confirmation Status in Redux
                    dispatch(updateEmailConfirmationStatus({ emailConfirmed: isEmailConfirmed }));
                    // 6.5. Mark email as confirmed in database (updates user_email_status table)
                    if (isEmailConfirmed) {
                        try {
                            console.log('[AuthCallback] Marking email as confirmed in database');
                            await supabase.rpc('mark_email_confirmed', { p_user_id: initialUser.id });
                            console.log('[AuthCallback] ✅ Email marked as confirmed in user_email_status table');
                        }
                        catch (dbError) {
                            console.error('[AuthCallback] ❌ Failed to mark email as confirmed in database:', dbError);
                            // Don't block flow if this fails - email is still confirmed in Supabase auth
                        }
                    }
                    // 7. Mark onboarding email_confirmation step as complete (if email is confirmed)
                    if (isEmailConfirmed && finalUser.role) {
                        try {
                            // First check if email_confirmation step is already complete
                            const statusEndpoint = finalUser.role === 'tutor'
                                ? `/api/tutor/onboarding/status/${finalUser.id}`
                                : `/api/student/onboarding/status/${finalUser.id}`;
                            console.log(`[AuthCallback] Checking onboarding status for ${finalUser.role}`);
                            const statusResponse = await axiosInstance.get(statusEndpoint);
                            const onboardingData = statusResponse.data.data || statusResponse.data;
                            const isEmailConfirmationComplete = onboardingData.email_confirmation_completed;
                            // Only mark as complete if not already complete
                            if (!isEmailConfirmationComplete) {
                                const completeStepEndpoint = finalUser.role === 'tutor'
                                    ? `/api/tutor/onboarding/complete-step/${finalUser.id}`
                                    : `/api/student/onboarding/complete-step/${finalUser.id}`;
                                console.log(`[AuthCallback] Marking email_confirmation step as complete for ${finalUser.role}`);
                                await axiosInstance.post(completeStepEndpoint, {
                                    step: 'email_confirmation'
                                });
                                console.log('[AuthCallback] ✅ Email confirmation step marked as complete in onboarding');
                            }
                            else {
                                console.log('[AuthCallback] ✅ Email confirmation step already complete, skipping');
                            }
                        }
                        catch (onboardingError) {
                            console.warn('[AuthCallback] Failed to mark onboarding email step as complete:', onboardingError);
                            // Don't fail the entire auth flow if this fails - user can still access dashboard
                            // The database trigger (if enabled) will serve as a fallback
                        }
                    }
                    return finalUser;
                };
                // Race against a 15s timeout to prevent hanging
                const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Session setup timed out')), 15000));
                const finalUser = await Promise.race([sessionSetupPromise(), timeoutPromise]);
                // 7. Success & Redirect
                setStatus('success');
                setMessage('Your email has been verified successfully!');
                toast({
                    title: "Email Verified",
                    description: "Your account has been verified successfully.",
                    variant: "default",
                });
                const dashboardPath = finalUser.role === 'tutor' ? '/tutor-dashboard' : '/student-dashboard';
                navigationTimer = setTimeout(() => {
                    navigate(dashboardPath, { replace: true });
                }, 1500);
            }
            catch (error) {
                console.error('[AuthCallback] Session setup error:', error);
                throw error;
            }
        };
        const handleEmailVerification = async () => {
            // DEBUG: Log URL details to catch hash stripping
            console.log('[AuthCallback] Processing URL:', window.location.href);
            try {
                // --- PRIORITY 1: CHECK HASH (Implicit Flow / 303 Redirect) ---
                // Supabase sends tokens in hash for 303 redirects
                // Check both React Router location and raw window location for robustness
                const rawHash = window.location.hash;
                const routerHash = location.hash;
                const hashString = (rawHash.length > routerHash.length ? rawHash : routerHash).replace(/^#/, '');
                const hashParams = new URLSearchParams(hashString);
                const hashAccessToken = hashParams.get('access_token');
                const hashRefreshToken = hashParams.get('refresh_token');
                const hashError = hashParams.get('error');
                const hashErrorDesc = hashParams.get('error_description');
                if (hashError) {
                    throw new Error(decodeURIComponent((hashErrorDesc || hashError).replace(/\+/g, ' ')));
                }
                if (hashAccessToken) {
                    console.log('[AuthCallback] Found token in hash');
                    const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                        access_token: hashAccessToken,
                        refresh_token: hashRefreshToken || ''
                    });
                    if (sessionError)
                        throw sessionError;
                    if (!sessionData.session)
                        throw new Error('Failed to establish session from token.');
                    await setupSessionAndRedirect(sessionData.session);
                    return;
                }
                // --- PRIORITY 2: CHECK PKCE CODE ---
                const searchParams = new URLSearchParams(location.search);
                let code = searchParams.get('code');
                // Fallback: Check if code is buried in hash (rare provider issue)
                if (!code && location.hash.includes('code=')) {
                    code = hashParams.get('code');
                }
                if (code) {
                    console.log('[AuthCallback] Found PKCE code');
                    const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
                    if (exchangeError) {
                        // Handle "Code Flow" errors (like scanner pre-clicks)
                        if (exchangeError.message?.includes('flow_state_not_found') ||
                            exchangeError.message?.includes('code_not_found')) {
                            // Check if actually verified despite error
                            const { data: { user } } = await supabase.auth.getUser();
                            if (user?.email_confirmed_at) {
                                setStatus('success');
                                setMessage('Email already verified. Redirecting...');
                                setTimeout(() => navigate('/login', { replace: true }), 2000);
                                return;
                            }
                        }
                        throw exchangeError;
                    }
                    if (data.session) {
                        await setupSessionAndRedirect(data.session);
                        return;
                    }
                }
                // --- PRIORITY 3: CHECK SEARCH PARAMS (Fallback) ---
                const searchAccessToken = searchParams.get('access_token');
                const searchRefreshToken = searchParams.get('refresh_token');
                if (searchAccessToken) {
                    console.log('[AuthCallback] Found token in search params');
                    const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                        access_token: searchAccessToken,
                        refresh_token: searchRefreshToken || ''
                    });
                    if (sessionError)
                        throw sessionError;
                    await setupSessionAndRedirect(sessionData.session);
                    return;
                }
                // --- NO TOKEN FOUND ---
                // If we reached here, no valid token was found in Hash, Search, or PKCE Code
                console.warn('[AuthCallback] No token found in URL');
                // Don't throw immediately, maybe we are already logged in?
                const { data: { session: existingSession } } = await supabase.auth.getSession();
                if (existingSession?.user?.email_confirmed_at) {
                    console.log('[AuthCallback] User already has confirmed session');
                    await setupSessionAndRedirect(existingSession);
                    return;
                }
                throw new Error('No verification token found. Please try clicking the link in your email again.');
            }
            catch (error) {
                console.error('[AuthCallback] Verification failed:', error);
                // RECOVERY CHECK: If verification failed (e.g. token expired/used), 
                // check if we are actually already logged in and verified.
                try {
                    const { data: { session: existingSession } } = await supabase.auth.getSession();
                    if (existingSession?.user?.email_confirmed_at) {
                        console.log('[AuthCallback] Recovered from error: User is already confirmed');
                        // Reuse the setup logic to redirect them correctly
                        await setupSessionAndRedirect(existingSession);
                        return;
                    }
                }
                catch (checkError) {
                    // If checking session fails, ignore and show the original error
                    console.warn('[AuthCallback] Session recovery check failed:', checkError);
                }
                setStatus('error');
                let friendlyMessage = error.message || 'Verification failed.';
                if (friendlyMessage.includes('expired') || friendlyMessage.includes('invalid') || friendlyMessage.includes('No verification token')) {
                    friendlyMessage = 'This link may be invalid, expired, or already used. If you have already verified your email, please try logging in.';
                }
                setMessage(friendlyMessage);
                // Only clear state if we definitely failed recovery
                dispatch(setUser({ user: null, session: null }));
            }
        };
        // LOGIC TO RUN VERIFICATION
        // We only run if we detect new tokens or haven't processed this exact URL state yet
        const currentHash = location.hash;
        const currentSearch = location.search;
        const searchParams = new URLSearchParams(currentSearch);
        const code = searchParams.get('code');
        const hasHashToken = currentHash.includes('access_token');
        // Check if we should process
        let shouldProcess = false;
        if (hasHashToken && currentHash !== processedHashRef.current) {
            shouldProcess = true;
            processedHashRef.current = currentHash;
        }
        else if (code && code !== processedCodeRef.current) {
            shouldProcess = true;
            processedCodeRef.current = code;
        }
        else if (searchParams.get('access_token')) {
            // Search params access token - treat as always processable or track similarly
            shouldProcess = true;
        }
        else if (!hasHashToken && !code && !processedHashRef.current && !processedCodeRef.current) {
            // Initial load with no tokens - try to process anyway to trigger "No token" fallback/recovery
            // But only if we haven't processed anything yet
            shouldProcess = true;
            // Mark as processed so we don't loop on empty URLs
            processedHashRef.current = 'checked_empty';
        }
        if (shouldProcess) {
            handleEmailVerification();
        }
        return () => {
            if (navigationTimer)
                clearTimeout(navigationTimer);
        };
    }, [location, navigate, dispatch]);
    return (_jsx("div", { className: "flex justify-center items-center min-h-screen bg-background p-4", children: _jsxs(Card, { className: "w-full max-w-md shadow-lg", children: [_jsxs(CardHeader, { children: [_jsx(CardTitle, { className: "text-center", children: "Email Verification" }), _jsx(CardDescription, { className: "text-center", children: status === 'loading' ? 'Verifying your email address...' :
                                status === 'success' ? 'Verification complete!' :
                                    'Verification failed' })] }), _jsx(CardContent, { children: _jsxs("div", { className: "flex flex-col items-center justify-center gap-6 py-4", children: [status === 'loading' && (_jsx(LoaderCircle, { className: "h-12 w-12 text-primary animate-spin" })), status === 'success' && (_jsx(CheckCircle, { className: "h-12 w-12 text-green-500 animate-in zoom-in duration-300" })), status === 'error' && (_jsx(XCircle, { className: "h-12 w-12 text-destructive animate-in zoom-in duration-300" })), _jsxs(Alert, { variant: status === 'error' ? "destructive" : "default", children: [_jsx(AlertTitle, { children: status === 'success' ? 'Success' : status === 'error' ? 'Error' : 'Processing' }), _jsx(AlertDescription, { children: message })] }), (status === 'error' || showManualAction) && (_jsxs("div", { className: "flex flex-col gap-3 w-full animate-in fade-in slide-in-from-bottom-4", children: [_jsx(Button, { onClick: () => navigate('/login'), children: "Return to Login" }), _jsx(Button, { variant: "outline", onClick: () => navigate('/auth/confirm-email'), children: "Resend Confirmation Email" }), _jsxs("div", { className: "mt-4 p-3 bg-muted rounded-md text-xs font-mono text-muted-foreground overflow-hidden break-all", children: [_jsx("p", { className: "font-bold mb-1", children: "Debug Info:" }), _jsxs("p", { children: ["URL: ", window.location.origin, window.location.pathname] }), _jsxs("p", { children: ["Hash Present: ", window.location.hash ? 'Yes' : 'No'] }), _jsxs("p", { children: ["Search Params: ", window.location.search ? 'Yes' : 'No'] }), _jsxs("p", { children: ["Ref: ", document.referrer ? new URL(document.referrer).hostname : 'None'] })] })] }))] }) })] }) }));
}
;
export default AuthCallback;
